import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { keyframes } from '@mui/material/styles';
import React from 'react';
import { ReactComponent as AllianceMark } from '../../assets/alliance/alliance-a.svg';
import { ReactComponent as AllianceLogo } from '../../assets/alliance/alliance-logo.svg';
import { ALLIANCE_APP_ORDER, ALLIANCE_APPS } from './alliance-apps';
import { pageSurface } from './alliance-surface';
import { HELP_DESK, STATUS_COLORS, SYSTEM_TONES } from './alliance-tokens';
import { AppTile } from './app-launcher';
import { AvatarTile, useIsMobileLayout } from './bar-controls';
import { CURRENT_APP, CURRENT_APP_FONTS } from './current-app';
import { appAvatarColors } from './recruit-link';

export type SystemTone = 'info' | 'warning' | 'error' | 'neutral' | 'imp';

export interface SystemAction {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  /** Opens in a new tab and gets the open_in_new end icon */
  external?: boolean;
  /** Lime impersonation button (Stop impersonating) */
  imp?: boolean;
}

/**
 * "Signed in as" on full-page screens. `full` when /authorize data is
 * available (name + NAA number), `token` otherwise (just the username).
 */
export type SystemIdentity =
  | { kind: 'full'; name: string; id: string }
  | { kind: 'token'; id: string };

const MONO = "'Roboto Mono', monospace";

const indeterminate = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(260%); }
`;

/** The indeterminate bar used by the splash (and nothing else moves) */
function IndeterminateBar(props: { width: number | string; color: string }) {
  return (
    <Box
      role="progressbar"
      aria-label="Loading"
      sx={{
        width: props.width,
        height: 3,
        borderRadius: '2px',
        backgroundColor: `${props.color}2e`,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '40%',
          borderRadius: '2px',
          backgroundColor: props.color,
          animation: `${indeterminate} 1.4s cubic-bezier(.4,0,.2,1) infinite`,
          '@media (prefers-reduced-motion: reduce)': {
            animationDuration: '2.8s',
          },
        }}
      />
    </Box>
  );
}

/**
 * Branded loading screen: the Alliance "A" and a 160×3 bar. The same markup
 * ships as static HTML in public/index.html so it paints before the bundle.
 *
 * @param caption - Only on the return from Cognito ("Signing you in…")
 */
export function SystemSplash(props: { caption?: string }) {
  const c = pageSurface();
  const barColor =
    CURRENT_APP.key === 'university' ? '#ffffff' : CURRENT_APP.accent;

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        backgroundColor: c.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        role="status"
        aria-label={props.caption || 'Loading'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '28px',
          color: c.logo,
        }}
      >
        <AllianceMark width={84} height={84} aria-hidden="true" />
        <IndeterminateBar width={160} color={barColor} />
        {props.caption ? (
          <Box sx={{ fontSize: 14, color: c.text2, mt: '-8px' }}>
            {props.caption}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** Date chip for the maintenance screen: tone background, schedule icon */
export function SystemDateChip(props: { children: React.ReactNode }) {
  const c = pageSurface();
  const tone = SYSTEM_TONES[c.dark ? 'dark' : 'light'].warning;
  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          p: '10px 16px 10px 12px',
          borderRadius: '12px',
          backgroundColor: tone.bg,
          border: `1px solid ${tone.line}`,
        }}
      >
        <ScheduleIcon sx={{ fontSize: 22, color: tone.fg }} />
        <Box sx={{ fontSize: 16, fontWeight: 500, color: c.text }}>
          {props.children}
        </Box>
      </Box>
    </Box>
  );
}

/** Lime "Viewing as" chip in the eyebrow slot of the noAgent screen */
export function ViewingAsChip(props: { name: string; id: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: 32,
        p: '0 12px 0 8px',
        borderRadius: '10px',
        backgroundColor: STATUS_COLORS.impersonating.main,
        color: STATUS_COLORS.impersonating.onMain,
        fontSize: 13,
        minWidth: 0,
      }}
    >
      <VisibilityIcon sx={{ fontSize: 16 }} />
      <Box
        component="span"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Viewing as{' '}
        <Box component="b" sx={{ fontWeight: 500 }}>
          {props.name}
        </Box>{' '}
        <Box
          component="span"
          sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700 }}
        >
          {props.id}
        </Box>
      </Box>
    </Box>
  );
}

/** Tone colors (imp = lime) for the icon tile, eyebrow and bullets */
const toneColors = (params: { tone: SystemTone; dark: boolean }) => {
  if (params.tone === 'imp') {
    return {
      fg: STATUS_COLORS.impersonating.onMain,
      bg: STATUS_COLORS.impersonating.main,
      line: STATUS_COLORS.impersonating.main,
      text: params.dark ? STATUS_COLORS.impersonating.main : '#9ccc00',
    };
  }
  const tone = SYSTEM_TONES[params.dark ? 'dark' : 'light'][params.tone];
  return { ...tone, text: tone.fg };
};

/** A 48px (page) action button: contained primary or outlined secondary */
function ActionButton(props: {
  action: SystemAction;
  variant: 'primary' | 'secondary';
  fullWidth: boolean;
  size?: 'large' | 'small';
}) {
  // Props
  const { action, variant } = props;
  const c = pageSurface();
  const isPrimary = variant === 'primary';
  const large = props.size !== 'small';

  const colors = action.imp
    ? {
        backgroundColor: STATUS_COLORS.impersonating.main,
        color: STATUS_COLORS.impersonating.onMain,
        border: '1px solid transparent',
      }
    : isPrimary
      ? {
          backgroundColor: c.primary.bg,
          color: c.primary.fg,
          border: '1px solid transparent',
        }
      : {
          backgroundColor: 'transparent',
          color: c.text,
          border: `1px solid ${c.border2}`,
        };

  const linkProps = action.href
    ? {
        component: 'a' as const,
        href: action.href,
        target: action.external ? '_blank' : undefined,
        rel: action.external ? 'noopener noreferrer' : undefined,
      }
    : {};

  // Primary icons trail the label; secondary icons lead it
  const endIcon = action.external ? (
    <OpenInNewIcon sx={{ fontSize: 18 }} />
  ) : isPrimary ? (
    action.icon
  ) : null;
  const startIcon = !isPrimary && !action.external ? action.icon : null;

  return (
    <ButtonBase
      {...linkProps}
      onClick={action.onClick}
      sx={{
        fontFamily: 'inherit',
        height: large ? 48 : 40,
        px: large ? '22px' : '16px',
        borderRadius: large ? '12px' : '10px',
        gap: large ? '10px' : 1,
        fontSize: large ? 15 : 14,
        fontWeight: 500,
        textDecoration: 'none',
        width: props.fullWidth ? '100%' : 'auto',
        ...colors,
        '& .MuiSvgIcon-root': { fontSize: 18 },
        '&:hover': isPrimary
          ? { filter: 'brightness(1.08)' }
          : { backgroundColor: c.hover },
      }}
    >
      {startIcon}
      {action.label}
      {endIcon}
    </ButtonBase>
  );
}

/**
 * One component for every system screen; each case is a short config
 * (see system-screens.tsx). Slots, top to bottom: status tile + eyebrow,
 * title, body (paragraphs, bullets, server message), embed
 * (children), app list, detail chip, "Signed in as", actions, Help Desk.
 *
 * Layouts:
 * - page: full viewport, logo top-left, app links row at the bottom
 * - inline: inside the shell (crash, 404, couldn't load), no logo or links
 * - compact: a section (tab, drawer, panel). Tile, one line, optional body
 *   and a small action
 *
 * @param tone - info, warning, error, neutral, or imp (lime, noAgent)
 * @param icon - A 26px icon for the tile
 * @param eyebrow - Short uppercase label (or a node, e.g. ViewingAsChip)
 * @param serverMessage - The API's message, shown verbatim in a box
 * @param identity - "Signed in as" above the actions (page layout only)
 * @param help - Help Desk block; "include" adds the "Please include…" line
 * @param appList - The other four Alliance apps (404)
 * @param dense - Compact layout inside a drawer (smaller tile and title)
 * @param inShell - The screen sits inside the App Bar shell (ARC goes dark)
 * @param showLinks - Page layout: the app links row (default true)
 */
export function SystemScreen(props: {
  layout: 'page' | 'inline' | 'compact';
  tone: SystemTone;
  icon: React.ReactNode;
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  body?: React.ReactNode;
  bullets?: string[];
  serverMessage?: string | null;
  detail?: React.ReactNode;
  identity?: SystemIdentity | null;
  primaryAction?: SystemAction | null;
  secondaryAction?: SystemAction | null;
  help?: boolean | 'include';
  appList?: boolean;
  dense?: boolean;
  inShell?: boolean;
  showLinks?: boolean;
  children?: React.ReactNode;
}) {
  // Props
  const { layout } = props;
  // Hooks
  const isMobile = useIsMobileLayout();

  const c = pageSurface({ inShell: props.inShell ?? layout !== 'page' });
  const tone = toneColors({ tone: props.tone, dark: c.dark });

  if (layout === 'compact') {
    return <CompactScreen {...props} />;
  }

  const isPage = layout === 'page';
  const hasActions = Boolean(props.primaryAction || props.secondaryAction);
  const titleFont = CURRENT_APP_FONTS.title as React.CSSProperties;

  const column = (
    <Box
      sx={{
        width: '100%',
        maxWidth: props.appList ? 520 : 480,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 3 : '28px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              flex: 'none',
              borderRadius: '12px',
              backgroundColor: tone.bg,
              color: tone.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiSvgIcon-root': { fontSize: 26 },
            }}
          >
            {props.icon}
          </Box>
          {typeof props.eyebrow === 'string' ? (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: tone.text,
              }}
            >
              {props.eyebrow}
            </Box>
          ) : (
            props.eyebrow
          )}
        </Box>
        <Box
          component="h1"
          sx={{
            m: 0,
            ...titleFont,
            fontSize: isMobile ? c.title.sizeMobile : c.title.size,
            lineHeight: c.title.lineHeight,
            letterSpacing: c.title.letterSpacing,
            textWrap: 'balance',
          }}
        >
          {props.title}
        </Box>
      </Box>

      {props.body || props.bullets || props.serverMessage ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            fontSize: 16,
            lineHeight: 1.55,
            color: c.text2,
            '& p': { m: 0, textWrap: 'pretty' },
            '& a': {
              color: c.link,
              fontWeight: 500,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
          }}
        >
          {props.body}
          {props.bullets ? (
            <Box
              component="ul"
              sx={{
                m: 0,
                p: 0,
                pl: '2px',
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {props.bullets.map((bullet) => (
                <Box
                  component="li"
                  key={bullet}
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    color: c.text,
                    fontWeight: 500,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '2px',
                      backgroundColor: tone.fg,
                      flex: 'none',
                    }}
                  />
                  {bullet}
                </Box>
              ))}
            </Box>
          ) : null}
          {props.serverMessage ? (
            <Box
              sx={{
                border: `1px solid ${c.border}`,
                backgroundColor: c.hover,
                borderRadius: '10px',
                p: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: c.text2,
                }}
              >
                Message from the server
              </Box>
              <Box sx={{ fontSize: 14, lineHeight: 1.45, color: c.text }}>
                {props.serverMessage}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {props.children}

      {props.appList ? <OtherApps /> : null}

      {props.detail}

      {isPage && props.identity && hasActions ? (
        <IdentityChip identity={props.identity} />
      ) : null}

      {hasActions ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
          }}
        >
          {props.primaryAction ? (
            <ActionButton
              action={props.primaryAction}
              variant="primary"
              fullWidth={isMobile}
            />
          ) : null}
          {props.secondaryAction ? (
            <ActionButton
              action={props.secondaryAction}
              variant="secondary"
              fullWidth={isMobile}
            />
          ) : null}
        </Box>
      ) : null}

      {props.help ? <HelpDesk include={props.help === 'include'} /> : null}
    </Box>
  );

  if (!isPage) {
    return (
      <Box
        sx={{
          color: c.text,
          letterSpacing: 'normal',
          p: isMobile ? '24px 16px' : '32px 40px',
          minHeight: { xs: 'calc(100dvh - 56px)', md: 'calc(100dvh - 64px)' },
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {column}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        backgroundColor: c.bg,
        color: c.text,
        letterSpacing: 'normal',
        display: 'flex',
        flexDirection: 'column',
        p: isMobile ? '28px 24px 24px' : '40px 56px',
        gap: isMobile ? 5 : 3,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flex: 'none',
        }}
      >
        <Box sx={{ color: c.logo, display: 'flex' }}>
          <AllianceLogo
            width={isMobile ? 79 : 99}
            height={isMobile ? 32 : 40}
            aria-label="The Alliance"
          />
        </Box>
        <Box
          sx={{
            minWidth: 0,
            fontSize: isMobile ? 11 : 13,
            fontWeight: 700,
            letterSpacing: '.12em',
            lineHeight: 1.3,
            textTransform: 'uppercase',
            textAlign: 'right',
            color: c.text2,
          }}
        >
          {CURRENT_APP.fullName}
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
        }}
      >
        {column}
      </Box>

      {props.showLinks === false ? null : <AppLinksRow />}
    </Box>
  );
}

/** Compact (section) layout: tile, title, body, small action */
function CompactScreen(props: React.ComponentProps<typeof SystemScreen>) {
  const c = pageSurface({ inShell: props.inShell ?? true });
  const tone = toneColors({ tone: props.tone, dark: c.dark });
  const tile = props.dense ? 32 : 36;
  const isAlert = props.tone === 'error';

  return (
    <Box
      role={isAlert ? 'alert' : 'status'}
      sx={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        p: props.dense ? '14px' : 2,
        borderRadius: '12px',
        border: `1px solid ${props.tone === 'info' ? tone.line : c.border}`,
        color: c.text,
        letterSpacing: 'normal',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: tile,
          height: tile,
          flex: 'none',
          borderRadius: props.dense ? '8px' : '10px',
          backgroundColor: tone.bg,
          color: tone.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: props.dense ? 18 : 20 },
        }}
      >
        {props.icon}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          pt: '1px',
        }}
      >
        <Box
          sx={{
            fontSize: props.dense ? 14 : 15,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {props.title}
        </Box>
        {props.body ? (
          <Box sx={{ fontSize: 14, lineHeight: 1.5, color: c.text2 }}>
            {props.body}
          </Box>
        ) : null}
        {props.serverMessage ? (
          <Box sx={{ fontSize: 14, lineHeight: 1.5, color: c.text2 }}>
            {props.serverMessage}
          </Box>
        ) : null}
        {props.primaryAction ? (
          <ButtonBase
            onClick={props.primaryAction.onClick}
            sx={{
              fontFamily: 'inherit',
              alignSelf: 'flex-start',
              mt: props.dense ? '6px' : 1,
              height: props.dense ? 32 : 36,
              px: props.dense ? '12px' : '14px',
              borderRadius: props.dense ? '8px' : '10px',
              border: `1px solid ${c.border2}`,
              gap: '6px',
              fontSize: props.dense ? 13 : 14,
              fontWeight: 500,
              '&:hover': { backgroundColor: c.hover },
            }}
          >
            {props.primaryAction.icon || (
              <RefreshIcon sx={{ fontSize: props.dense ? 16 : 18 }} />
            )}
            {props.primaryAction.label}
          </ButtonBase>
        ) : null}
      </Box>
    </Box>
  );
}

/** "Signed in as" chip, so a Help Desk screenshot always shows who it is */
function IdentityChip(props: { identity: SystemIdentity }) {
  const c = pageSurface();
  const avatar = appAvatarColors();
  const { identity } = props;

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          p: '6px 14px 6px 6px',
          borderRadius: '12px',
          border: `1px solid ${c.border}`,
          backgroundColor: c.paper,
          minWidth: 0,
        }}
      >
        {identity.kind === 'full' ? (
          <AvatarTile
            initials={identity.name
              .split(' ')
              .map((word) => word[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
            size={28}
            background={avatar.background}
            color={avatar.color}
          />
        ) : (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: avatar.background,
              color: avatar.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <PersonIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
        <Box sx={{ fontSize: 14, lineHeight: 1.35, color: c.text2 }}>
          Signed in as{' '}
          {identity.kind === 'full' ? (
            <React.Fragment>
              <Box component="span" sx={{ color: c.text, fontWeight: 500 }}>
                {identity.name}
              </Box>{' '}
              ·{' '}
            </React.Fragment>
          ) : null}
          <Box
            component="span"
            sx={{
              fontFamily: MONO,
              fontSize: identity.kind === 'full' ? 12 : 13,
              fontWeight: 700,
              color: c.link,
            }}
          >
            {identity.id}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Help Desk block: email, hours and the optional "Please include…" line */
function HelpDesk(props: { include: boolean }) {
  const c = pageSurface();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '14px',
        pt: 3,
        borderTop: `1px solid ${c.border}`,
      }}
    >
      <SupportAgentIcon sx={{ fontSize: 22, color: c.text2 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <Box sx={{ fontWeight: 500 }}>Help Desk</Box>
        <Box sx={{ color: c.text2 }}>
          <Box
            component="a"
            href={`mailto:${HELP_DESK.email}`}
            sx={{
              color: c.link,
              fontWeight: 500,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}
          >
            {HELP_DESK.email}
          </Box>{' '}
          · {HELP_DESK.hours}
        </Box>
        {props.include ? (
          <Box sx={{ color: c.text2 }}>{HELP_DESK.include}</Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** The other four apps (404), same tiles as the launcher */
function OtherApps() {
  const c = pageSurface({ inShell: true });
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${c.border}`,
        borderRadius: '14px',
        backgroundColor: c.paper,
        p: '6px',
      }}
    >
      {ALLIANCE_APP_ORDER.filter((key) => key !== CURRENT_APP.key).map(
        (key) => {
          const app = ALLIANCE_APPS[key];
          return (
            <Box
              key={key}
              component="a"
              href={app.url}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                p: '8px 10px',
                borderRadius: '10px',
                textDecoration: 'none',
                color: c.text,
                '&:hover': { backgroundColor: c.hover },
              }}
            >
              <AppTile appKey={key} />
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <Box sx={{ fontSize: 15, fontWeight: 500 }}>{app.name}</Box>
                <Box sx={{ fontSize: 13, color: c.text2 }}>
                  {app.description}
                </Box>
              </Box>
              <OpenInNewIcon sx={{ fontSize: 18, color: c.text2 }} />
            </Box>
          );
        },
      )}
    </Box>
  );
}

/**
 * The Sign In page's app links (no social links), so a blocked user can
 * still reach the other sites. Phones leave out the current app.
 */
function AppLinksRow() {
  // Hooks
  const isMobile = useIsMobileLayout();
  const c = pageSurface();

  return (
    <Box
      component="nav"
      aria-label="Alliance apps"
      sx={{
        flex: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 16px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
      }}
    >
      {ALLIANCE_APP_ORDER.filter(
        (key) => !isMobile || key !== CURRENT_APP.key,
      ).map((key) => (
        <Box
          key={key}
          component="a"
          href={ALLIANCE_APPS[key].url}
          sx={{
            color: key === CURRENT_APP.key ? c.text : c.text2,
            textDecoration: 'none',
            '&:hover': { color: c.text },
          }}
        >
          {ALLIANCE_APPS[key].name}
        </Box>
      ))}
    </Box>
  );
}
